import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { OrpcService } from '../../../shared/services/orpc-client.service';
import type { contractOutput } from '#backend/contract';
import { CnpjPipe } from '../../../shared/cnpj.pipe';

@Component({
  selector: 'app-contract-view',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CnpjPipe,
  ],
  templateUrl: './contract-view.component.html',
  styleUrls: ['./contract-view.component.scss'],
})
export class ContractViewComponent implements OnInit {
  private orpc = inject(OrpcService).client;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);

  contract: contractOutput['contract']['byId'] | null = null;
  isLoading = true;
  contractId!: number;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.contractId = parseInt(id, 10);
      this.loadContract();
    } else {
      this.snackBar.open('ID do contrato não encontrado', 'Fechar', {
        duration: 3000,
      });
      this.router.navigate(['/admin/contracts']);
    }
  }

  async loadContract(): Promise<void> {
    try {
      this.contract = await this.orpc.contract.byId(this.contractId);
      if (!this.contract) {
        this.snackBar.open('Contrato não encontrado', 'Fechar', {
          duration: 3000,
        });
        this.router.navigate(['/admin/contracts']);
      }
    } catch (error) {
      console.error('Error loading contract:', error);
      this.snackBar.open('Erro ao carregar contrato', 'Fechar', {
        duration: 3000,
      });
      this.router.navigate(['/admin/contracts']);
    } finally {
      this.isLoading = false;
    }
  }

  onEdit(): void {
    this.router.navigate(['/admin/contracts/edit', this.contractId]);
  }

  getModalityLabel(modality: string): string {
    return modality === 'with_commitment'
      ? 'Com compromisso de consumo'
      : 'Sem compromisso de consumo';
  }

  getModalityChipClass(modality: string): string {
    return modality === 'with_commitment'
      ? 'modality-with-commitment'
      : 'modality-without-commitment';
  }

  getContractStatus(): string {
    if (!this.contract) return 'Desconhecido';

    const now = new Date();
    const startDate = new Date(this.contract.startDate + 'T00:00:00-03:00');
    const endDate = new Date(this.contract.endDate + 'T23:59:59-03:00');

    if (now < startDate) {
      return 'Não iniciado';
    } else if (now > endDate) {
      return 'Expirado';
    } else {
      return 'Ativo';
    }
  }

  getStatusChipClass(): string {
    const status = this.getContractStatus();
    switch (status) {
      case 'Ativo':
        return 'status-active';
      case 'Não iniciado':
        return 'status-pending';
      case 'Expirado':
        return 'status-expired';
      default:
        return 'status-unknown';
    }
  }

  getPeriodDurationInDays(): number {
    if (!this.contract) return 0;

    const start = new Date(this.contract.startDate + 'T00:00:00-03:00');
    const end = new Date(this.contract.endDate + 'T23:59:59-03:00');
    const timeDiff = end.getTime() - start.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  canEdit(): boolean {
    if (!this.contract) return false;

    const now = new Date();
    const endDate = new Date(this.contract.endDate + 'T23:59:59-03:00');

    // Permite edição apenas se o contrato ainda não expirou
    return now <= endDate;
  }
}
